import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { usePopup } from '../context/PopupContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const { openPopup } = usePopup();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const services = [
    { name: 'Trademark Registration', path: '/services/trademark-registration' },
    { name: 'Copyright Registration', path: '/services/copyright-registration' },
    { name: 'Company Formation', path: '/services/company-formation' },
    { name: 'Trademark Search', path: '/services/trademark-search' },
    { name: 'Amazon Brand Registry', path: '/services/amazon-brand-registry' }
  ];

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
        isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 max-w-[1320px]">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img 
              src="/assets/img/brand/logo-dark.png" 
              alt="Trademark Vector" 
              className="h-8 sm:h-10 md:h-12 w-auto transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium ${
                location.pathname === '/' ? 'text-[#C0392B]' : ''
              }`}
            >
              Home
            </Link>
            
            <div 
              className="relative group"
              onMouseEnter={() => setIsServicesOpen(true)}
              onMouseLeave={() => setIsServicesOpen(false)}
            >
              <button className="flex items-center space-x-1 text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium">
                <span>Services</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {isServicesOpen && (
                <div className="absolute top-full left-0 pt-2 w-64">
                  <div className="bg-white rounded-lg shadow-xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2">
                    {services.map((service) => (
                      <Link
                        key={service.path}
                        to={service.path}
                        className="block px-4 py-2 text-[#2C3E50] hover:bg-[#FDFEFE] hover:text-[#C0392B] transition-colors"
                        onClick={() => setIsServicesOpen(false)}
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link 
              to="/about" 
              className={`text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium ${
                location.pathname === '/about' ? 'text-[#C0392B]' : ''
              }`}
            >
              About Us
            </Link>
            
            <Link 
              to="/contact" 
              className={`text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium ${
                location.pathname === '/contact' ? 'text-[#C0392B]' : ''
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button 
              className="bg-[#C0392B] hover:bg-[#A93226] text-white px-6 py-2 rounded-md transition-all duration-300 hover:shadow-lg btn-glow-hover"
              onClick={() => {
                if (window.LiveChatWidget) {
                  window.LiveChatWidget.call('maximize');
                }
              }}
            >
              Live Chat
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-[#2C3E50] p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden py-4 border-t border-gray-200 animate-in slide-in-from-top">
            <nav className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className="text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              
              <div className="flex flex-col space-y-2">
                <span className="text-[#2C3E50] font-medium">Services</span>
                {services.map((service) => (
                  <Link
                    key={service.path}
                    to={service.path}
                    className="pl-4 text-[#7F8C8D] hover:text-[#C0392B] transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {service.name}
                  </Link>
                ))}
              </div>

              <Link 
                to="/about" 
                className="text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                About Us
              </Link>
              
              <Link 
                to="/contact" 
                className="text-[#2C3E50] hover:text-[#C0392B] transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>

              <Button 
                className="bg-[#C0392B] hover:bg-[#A93226] text-white w-full"
                onClick={() => {
                  setIsMenuOpen(false);
                  if (window.LiveChatWidget) {
                    window.LiveChatWidget.call('maximize');
                  }
                }}
              >
                Live Chat
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;